import React from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Pencil, Save, X, Loader2 } from "lucide-react";

const EDITABLE = new Set(["new", "contacted"]);

export default function NotesEditor({ booking, onUpdate }) {
  const editable = EDITABLE.has(booking.status || "new");
  const [editing, setEditing] = React.useState(false);
  const [value, setValue] = React.useState(booking.notes || "");
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => { setValue(booking.notes || ""); }, [booking.notes]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch(`/my-bookings/${booking.id}/notes`, { notes: value });
      toast.success("Catatan tersimpan");
      setEditing(false);
      onUpdate(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setValue(booking.notes || "");
    setEditing(false);
  };

  return (
    <div className="mt-5 p-4 bg-muted/40 rounded border-l-2 border-accent" data-testid={`notes-editor-${booking.id}`}>
      <div className="flex items-center justify-between mb-2">
        <p className="overline">Catatan Anda</p>
        {editable && !editing && (
          <button
            onClick={() => setEditing(true)}
            className="text-xs text-accent hover:text-primary inline-flex items-center gap-1"
            data-testid={`edit-notes-${booking.id}`}
          >
            <Pencil className="h-3 w-3" /> Edit
          </button>
        )}
      </div>
      {editing ? (
        <>
          <textarea
            value={value}
            onChange={(e) => setValue(e.target.value)}
            rows={4}
            maxLength={2000}
            data-testid={`notes-textarea-${booking.id}`}
            className="w-full bg-white border border-border rounded p-3 text-sm text-primary focus:outline-none focus:ring-2 focus:ring-accent"
            placeholder="Tambahkan catatan untuk tim BATIK (mis. permintaan diet, kebutuhan khusus, kursi roda, dsb)"
          />
          <div className="flex items-center justify-between mt-2">
            <span className="text-xs text-muted-foreground">{value.length}/2000</span>
            <div className="flex gap-2">
              <button onClick={cancel} className="text-xs px-3 py-1.5 rounded-full border border-border hover:bg-muted inline-flex items-center gap-1" data-testid={`cancel-notes-${booking.id}`}>
                <X className="h-3 w-3" /> Batal
              </button>
              <button
                onClick={save}
                disabled={saving}
                className="text-xs px-4 py-1.5 rounded-full bg-primary text-white hover:bg-primary/90 disabled:opacity-60 inline-flex items-center gap-1"
                data-testid={`save-notes-${booking.id}`}
              >
                {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Simpan
              </button>
            </div>
          </div>
        </>
      ) : (
        <>
          <p className="text-sm text-muted-foreground whitespace-pre-wrap">
            {booking.notes || <span className="italic">Belum ada catatan. {editable && "Klik Edit untuk menambah."}</span>}
          </p>
          {!editable && (
            <p className="mt-2 text-[10px] uppercase tracking-widest text-muted-foreground/70">
              Read-only — booking sudah {booking.status}
            </p>
          )}
        </>
      )}
    </div>
  );
}
